import { C as FARBIG, PALETTES, FONT, FONT_MEDIUM, FONT_BOLD, FONT_ITALIC } from './tokens.js';
import { vectorSource, treeSource, GLYPHS, SPRITE } from './tiles.js';


const SWISSTOPO_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">&copy; swisstopo</a>';
// SWISSIMAGE stammt teils aus Satellitenaufnahmen und verlangt laut swisstopo
// eine eigene Quellenangabe - nicht bloss "swisstopo".
const SWISSIMAGE_ATTRIB =
  '<a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">' +
  'CNES, Spot Image, swisstopo, NPOC</a>';
const WMTS = (layer, fmt = 'png') =>
  `https://wmts.geo.admin.ch/1.0.0/${layer}/default/current/3857/{z}/{x}/{y}.${fmt}`;

/**
 * Groesse der Symbolplaketten. Sie waechst mit dem Zoom wie die Schrift auch -
 * eine feste Zahl wirkt weit draussen zu schwer und nah zu zierlich. Die
 * Plakette misst 24 px, der Faktor bringt sie auf das Mass der urspruenglichen
 * OSM-Zeichen (rund 20 px) und darueber hinaus.
 */
const ICON = ['interpolate', ['linear'], ['zoom'], 12, 0.6, 15, 0.8, 18, 1];

/** Zoomabhängige Linienbreite: [[z, w], ...] -> interpolate-Ausdruck */
const w = (stops) => ['interpolate', ['linear'], ['zoom'], ...stops.flat()];

/**
 * Schriftgrad waechst exponentiell mit, nicht linear - sonst wirkt die
 * Beschriftung beim Hineinzoomen schnell zu klein. Basis 1.25 wie in
 * swisstopos eigenen Vektorkacheln; die Stuetzwerte sind von dort abgelesen.
 */
const ts = (stops) => ['interpolate', ['exponential', 1.25], ['zoom'], ...stops.flat()];

/**
 * Deckkraft aller Gebaeudeebenen - eine Kurve, von niemandem abweichend.
 * Sie steigt durchgehend; ein Zwischenhoch wuerde beim Hineinzoomen als
 * zweite Stufe gelesen, auch wenn der Endwert stimmt.
 *
 * Kurz gehalten: ein Drittel einer Zoomstufe. Lang genug, dass nichts
 * aufspringt, kurz genug, dass man nicht minutenlang halbdurchsichtige
 * Haeuser ansieht.
 */
const GEBAEUDE_DECKUNG = ['interpolate', ['linear'], ['zoom'], 14, 0, 14.3, 1];

/** Gleise haben eine Breite, nicht mehrere - darum feste Werte statt Rampe. */
const RAIL_W = 1.6;
const RAIL_LIGHT_W = 1.1;

/** Beschriftung bevorzugt deutsch, sonst lokaler Name */
/**
 * Beschriftung: der amtliche Name vor dem deutschen Exonym.
 *
 * `name:de` wirkte zunaechst richtig, setzt aber die deutsche Uebersetzung
 * durch - gemessen in OSM: St. Gallen traegt `name=St. Gallen`, aber
 * `name:de=Sankt Gallen`, und Milano wird so zu Mailand. Eine Landeskarte
 * schreibt Orte so an, wie sie vor Ort heissen. `name` liefert genau das,
 * auch bei zweisprachigen Gemeinden wie Biel/Bienne.
 *
 * Unbedenklich, weil die Kacheln nur die Schweiz und ihre Nachbarn abdecken -
 * alles in lateinischer Schrift. Fuer eine Weltkarte braeuchte es `name:latin`.
 */
const NAME = ['coalesce', ['get', 'name'], ['get', 'name:de'], ['get', 'name_de']];

/**
 * Wichtige Punkte früh, Nebensächliches spät. Haltestellen sind in OSM sehr
 * dicht und kämen sonst schon bei Zoom 14 flächendeckend.
 */
/**
 * Farbgruppen der POI-Beschriftung, uebernommen aus swisstopos Vektorstil.
 * Dort ist der Normalfall fast schwarz; Farbe tragen nur diese Gruppen.
 * Alles Uebrige - Gastronomie, Laeden, Banken, Hotels - bleibt neutral.
 */
const POI_FARBGRUPPEN = [
  ['poiVerkehr', ['bus', 'railway', 'rail', 'tram', 'subway', 'aerialway', 'airport',
    'aerodrome', 'ferry_terminal', 'harbor', 'parking', 'bicycle_parking',
    'bicycle_rental', 'car_rental', 'car_sharing', 'charging_station', 'taxi', 'fuel']],
  ['poiHistorisch', ['place_of_worship', 'monastery', 'attraction', 'monument', 'castle',
    'ruins', 'memorial', 'artwork', 'museum', 'art_gallery', 'theatre']],
  ['poiNatur', ['park', 'cemetery', 'viewpoint', 'picnic_site', 'playground', 'pitch',
    'garden', 'dog_park', 'recycling', 'pharmacy', 'camp_site', 'golf']],
  ['poiAmtlich', ['embassy', 'courthouse', 'prison', 'townhall', 'police', 'library',
    'fire_station', 'post']],
  ['poiWasser', ['hospital', 'clinic', 'swimming_pool', 'marina', 'waterfall', 'water_park']],
  ['poiHuette', ['alpine_hut', 'wilderness_hut', 'shelter']],
  ['poiBildung', ['school', 'college', 'university', 'kindergarten']]
];

/** Aus den Gruppen einen `match`-Ausdruck bauen. */
const poiFarbe = (C) => [
  'match', ['get', 'class'],
  ...POI_FARBGRUPPEN.flatMap(([token, klassen]) => [klassen, C[token] || C.poi]),
  C.poi
];

export const POI_SPLIT = {
  'poi-symbol': ['all', ['<=', ['get', 'rank'], 8], ['!=', ['get', 'class'], 'bus']],
  'poi-minor': ['any', ['>', ['get', 'rank'], 8], ['==', ['get', 'class'], 'bus']]
};

const fill = (id, sourceLayer, filter, color, extra = {}) => ({
  id,
  type: 'fill',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  paint: { 'fill-color': color, 'fill-antialias': true, ...extra }
});

const line = (id, sourceLayer, filter, paint, layout = {}) => ({
  id,
  type: 'line',
  source: 'osm',
  'source-layer': sourceLayer,
  ...(filter ? { filter } : {}),
  layout: { 'line-cap': 'round', 'line-join': 'round', ...layout },
  paint
});

/**
 * Strassenklassen. In kleinen Massstäben bleiben die Linien schlank und
 * wachsen erst ab Zoom 14 auf die Breite, die im Kartenbild gewünscht ist.
 */
const roadClasses = (C) => ({
  motorway: {
    case: C.motorwayCase, fill: C.motorway, minzoom: 5,
    width: [[5, 0.6], [8, 1.2], [10, 1.8], [12, 3.2], [13, 4.2], [14, 6.5], [15, 11], [16, 20], [20, 48]]
  },
  trunk: {
    case: C.trunkCase, fill: C.trunk, minzoom: 6,
    width: [[6, 0.5], [9, 1.1], [12, 2.6], [14, 5], [16, 14], [20, 36]]
  },
  primary: {
    case: C.primaryCase, fill: C.primary, minzoom: 7,
    width: [[7, 0.5], [10, 1.2], [12, 2.4], [14, 4.6], [16, 12], [20, 32]]
  },
  secondary: {
    case: C.secondaryCase, fill: C.secondary, minzoom: 9,
    width: [[9, 0.5], [12, 1.8], [14, 3.2], [16, 9], [20, 24]]
  },
  tertiary: {
    case: C.tertiaryCase, fill: C.tertiary, minzoom: 10,
    width: [[10, 0.5], [13, 1.6], [16, 8], [20, 20]]
  },
  minor: {
    case: C.minorCase, fill: C.minor, minzoom: 12,
    width: [[12, 0.7], [14, 2.2], [16, 6], [20, 18]]
  },
  service: {
    case: C.serviceCase, fill: C.service, minzoom: 14,
    width: [[14, 0.9], [16, 3.2], [20, 12]]
  }
});

/** Der Rand wächst mit der Linie mit, statt bei kleinem Massstab zu dominieren. */
const casingWidth = (stops, factor) => stops.map(([z, v]) => [z, v * factor + 0.5]);

function roadLayers(C) {
  const ROAD_CLASSES = roadClasses(C);
  const layers = [];
  const order = ['service', 'minor', 'tertiary', 'secondary', 'primary', 'trunk', 'motorway'];

  // Erst alle Casings, dann alle Füllungen -> saubere Kreuzungen
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}-case`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        {
          'line-color': r.case,
          'line-width': w(casingWidth(r.width, key === 'motorway' ? 1.4 : 1.32))
        },
        { 'line-cap': 'butt' }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  for (const key of order) {
    const r = ROAD_CLASSES[key];
    layers.push(
      line(
        `road-${key}`,
        'transportation',
        ['all', ['==', ['get', 'class'], key], ['!=', ['get', 'brunnel'], 'tunnel']],
        { 'line-color': r.fill, 'line-width': w(r.width) }
      )
    );
    layers[layers.length - 1].minzoom = r.minzoom;
  }
  return layers;
}

/**
 * Ein- und Ausblenden statt Umschalten.
 *
 * MapLibre zeichnet eine Ebene unterhalb ihres `minzoom` gar nicht - sie
 * erscheint also schlagartig. Hier bekommt jede Ebene mit Zoomgrenze eine
 * Deckkraft-Rampe ueber eine halbe Zoomstufe, damit nichts aufspringt.
 *
 * Ebenen, die schon eine eigene Rampe mitbringen, bleiben unberuehrt: dort war
 * der Verlauf Absicht und meist sorgfaeltiger gewaehlt als eine Faustregel.
 */
const EINBLENDEN = 0.35;

const DECKKRAFT = {
  fill: ['fill-opacity'],
  line: ['line-opacity'],
  symbol: ['text-opacity', 'icon-opacity'],
  raster: ['raster-opacity'],
  'fill-extrusion': ['fill-extrusion-opacity'],
  circle: ['circle-opacity', 'circle-stroke-opacity']
};

function weicheUebergaenge(layers) {
  for (const l of layers) {
    const schluessel = DECKKRAFT[l.type];
    if (!schluessel) continue;
    const min = Number.isFinite(l.minzoom) ? l.minzoom : null;
    const max = Number.isFinite(l.maxzoom) ? l.maxzoom : null;
    if (min === null && max === null) continue;

    // Bei engen Fenstern die Spanne kuerzen, sonst ueberschneiden sich die
    // Stuetzpunkte und der Ausdruck ist ungueltig.
    const spanne = min !== null && max !== null
      ? Math.min(EINBLENDEN, (max - min) / 2.5)
      : EINBLENDEN;
    if (spanne <= 0.01) continue;

    l.paint = { ...(l.paint || {}) };
    for (const k of schluessel) {
      const jetzt = l.paint[k];
      if (Array.isArray(jetzt)) continue;
      const voll = typeof jetzt === 'number' ? jetzt : 1;
      const stops = [];
      if (min !== null) stops.push(min, 0, min + spanne, voll);
      if (max !== null) stops.push(max - spanne, voll, max, 0);
      if (stops.length < 4) continue;
      l.paint[k] = ['interpolate', ['linear'], ['zoom'], ...stops];
    }
  }
  return layers;
}

export function buildStyle({ palette = 'farbig' } = {}) {
  const C = PALETTES[palette] || FARBIG;
  // Breite wie bei swisstopo zoomabhaengig: in der Uebersicht fast nichts,
  // im grossen Massstab knapp ueber einem Pixel. Das Luftbild hebt sie an.
  const hf = C.haloFactor ?? 1;
  const haloW = (max) => w([[12, 0.5 * hf], [14, 0.9 * hf], [16, max * hf]]);
  /**
   * Ortsnamen wachsen bis 30 px - ein Schleier von 1,25 px traegt die dann
   * nicht mehr. Er waechst deshalb mit: auf hellem Grund bleibt er unsichtbar,
   * auf schwarzen Gebaeudebloecken macht er die Schrift ueberhaupt erst lesbar.
   */
  const haloPlace = w([[12, 0.6 * hf], [14, 1.3 * hf], [16, 2.1 * hf], [18, 2.8 * hf]]);
  return {
    version: 8,
    name: 'KGVA Landeskarte',
    glyphs: GLYPHS,
    sprite: SPRITE,
    sources: {
      osm: vectorSource(),
      // Relief als *Vektor*: Schummerung, Felszeichnung und Geroell. swisstopo
      // fuehrt das neben der Basiskarte als eigene Quelle - Ausdehnung
      // 3.57..13.66 Ost, 44.18..48.88 Nord, also samt Nachbarlaendern.
      // Dadurch braucht es weder eine eigene Rechnerei noch ein Rasterbild.
      reliefVektor: {
        type: 'vector',
        url: 'https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.relief.vt/v1.0.0/tiles.json',
        attribution: SWISSTOPO_ATTRIB
      },

      aerial: {
        type: 'raster',
        tiles: [WMTS('ch.swisstopo.swissimage', 'jpeg')],
        tileSize: 256,
        minzoom: 2,
        maxzoom: 19,
        attribution: SWISSIMAGE_ATTRIB
      },
      // Offizielle Vektorkacheln von swisstopo: Hoehenkurven, Hoehenkoten und
      // Flurnamen aus der Landeskarte - genauer als alles aus einem globalen
      // Hoehenmodell gerechnete, aber nur fuer die Schweiz.
      swisstopo: {
        type: 'vector',
        url: 'https://vectortiles.geo.admin.ch/tiles/ch.swisstopo.base.vt/v1.0.0/tiles.json',
        attribution: SWISSTOPO_ATTRIB
      },
      // Hoehenmodell fuer die Gelaendedarstellung. Kostet nichts, solange
      // `setTerrain` nicht gesetzt ist - dann wird keine Kachel geladen.
      gelaende: {
        type: 'raster-dem',
        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
        encoding: 'terrarium',
        tileSize: 256,
        // Kacheln gibt es bis z15, aber darueber hinaus gewinnt man nichts:
        // die Quelldaten sind groeber als das Gitter, und MapLibre glaettet
        // beim Ueberzoomen besser, als die Stufen es zulassen.
        maxzoom: 13,
        attribution:
          '<a href="https://registry.opendata.aws/terrain-tiles/" target="_blank" rel="noopener">Terrain Tiles</a>'
      },
      ...(treeSource() ? { trees: treeSource() } : {})
    },
    layers: weicheUebergaenge([
      { id: 'background', type: 'background', paint: { 'background-color': C.paper } },

      // Umschaltbare Hintergründe (nur Schweiz), standardmässig aus
      {
        id: 'bg-aerial',
        type: 'raster',
        source: 'aerial',
        layout: { visibility: 'none' },
        paint: { 'raster-opacity': 1 }
      },

      // ---------- Bodenbedeckung ----------
      // Unter Zoom 14 liefern die Kacheln kaum Gebäude – dann trägt die
      // Siedlungsfläche das Bild, wie im kleinen Massstab der Landeskarte auch.
      fill('lc-settlement', 'landuse', ['match', ['get', 'class'], ['residential', 'suburb', 'quarter', 'neighbourhood'], true, false], C.settlement, {
        // Die Siedlungsflaeche vertritt die Gebaeude, solange es keine gibt.
        // Sie nimmt sich ueber genau dieselbe Spanne zurueck, in der die
        // Gebaeude aufblenden (14 bis 15.2) - was die eine verliert, gewinnt
        // die andere.
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0.55, 11, 1, 14, 1, 14.3, 0.4, 16, 0.35]
      }),
      fill('lc-farmland', 'landcover', ['==', ['get', 'class'], 'farmland'], C.farmland),
      fill('lc-grass', 'landcover', ['match', ['get', 'class'], ['grass', 'wetland'], true, false], C.grass),
      fill('lc-sand', 'landcover', ['==', ['get', 'class'], 'sand'], C.sand),
      fill('lc-rock', 'landcover', ['==', ['get', 'class'], 'rock'], C.rock),
      fill('lc-glacier', 'landcover', ['==', ['get', 'class'], 'ice'], C.glacier),
      fill('lc-scrub', 'landcover', ['match', ['get', 'subclass'], ['scrub', 'heath'], true, false], C.scrub),
      fill('lc-forest', 'landcover', ['==', ['get', 'class'], 'wood'], C.forest),
      line('lc-forest-edge', 'landcover', ['==', ['get', 'class'], 'wood'], {
        'line-color': C.forestEdge,
        'line-width': w([[10, 0.4], [14, 1]]),
        'line-opacity': 0.8
      }),
      fill('lc-park', 'park', null, C.grass, { 'fill-opacity': 0.45 }),

      // ---------- Relief (optional, nur CH) ----------
      // Schummerung aus den Vektorkacheln. Die Flaechen tragen eine Helligkeit
      // von -15 bis 0; swisstopos Farbtreppe dazu ist hier uebernommen, damit
      // das Bild dem Original entspricht. `fill-antialias: false` ist wichtig -
      // sonst zeichnet MapLibre an jeder Flaechenkante eine feine Linie, und
      // aus der Schummerung wird ein Netz.
      {
        id: 'relief-global',
        type: 'fill',
        source: 'reliefVektor',
        'source-layer': 'hillshade',
        filter: ['<=', ['get', 'luminosity'], 0],
        layout: { visibility: 'none' },
        paint: {
          'fill-antialias': false,
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 1, 0.5, 8, 1],
          'fill-color': ['match', ['get', 'luminosity'],
            -15, '#ADBCC7', -14, '#B2C0CB', -13, '#B7C5CE', -12, '#BDC9D2',
            -11, '#C2CDD5', -10, '#C7D1D9', -9, '#CCD6DC', -8, '#D1DAE0',
            -7, '#D7DEE3', -6, '#DCE2E7', -5, '#E1E7EA', -4, '#E6EBEE',
            -3, '#EBEFF1', -2, '#F1F3F5', -1, '#F6F8F8', 0, '#FBFCFC',
            '#FBFCFC']
        }
      },
      // Felszeichnung - der Stiftstrich, der die Landeskarte ausmacht.
      // Klasse 1 ist Fels, alles Uebrige Eis.
      {
        id: 'relief-hachure',
        type: 'fill',
        source: 'reliefVektor',
        'source-layer': 'hachure',
        filter: ['==', ['geometry-type'], 'Polygon'],
        layout: { visibility: 'none' },
        paint: {
          'fill-antialias': false,
          'fill-color': ['match', ['get', 'class'], 1, '#0C0C0C', '#1985C8'],
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 9, 0, 11, 0.27]
        }
      },
      // ---------- Einzelbäume ----------
      // Die Landeskarte hebt einzelne Bäume hervor; im OpenMapTiles-Schema
      // fehlen sie, darum ein eigenes kleines Archiv (deploy/trees.yml).
      {
        id: 'tree-row',
        type: 'line',
        source: 'trees',
        'source-layer': 'tree_row',
        minzoom: 15,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': C.tree,
          'line-width': w([[15, 1.4], [18, 3]]),
          'line-dasharray': [0.1, 1.6]
        }
      },
      {
        id: 'tree-single',
        type: 'circle',
        source: 'trees',
        'source-layer': 'tree',
        minzoom: 15,
        paint: {
          'circle-color': ['match', ['get', 'leaf_type'], 'needleleaved', C.treeConifer, C.tree],
          'circle-radius': w([[15, 1], [17, 2.1], [19, 3.4]]),
          'circle-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0.55, 16.5, 0.9]
        }
      },

      // ---------- Höhenlinien ----------
      // Zwischenkurven alle 10 m, Hauptkurven alle 100 m - so zeichnet es die
      // Landeskarte. Die Werte kommen direkt aus swisstopos Kurvensatz.
      {
        id: 'contour-minor',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 12,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['!=', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contour,
          'line-width': w([[12, 0.4], [16, 0.7], [18, 0.9]]),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0.3, 14, 0.75]
        }
      },
      {
        id: 'contour-main',
        type: 'line',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 10,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': C.contourMain,
          'line-width': w([[10, 0.7], [14, 1.1], [18, 1.6]]),
          'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0.4, 13, 0.8]
        }
      },

      {
        id: 'contour-label',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'contour_line',
        minzoom: 12.5,
        filter: ['all', ['==', ['get', 'class'], 'land'], ['==', ['%', ['get', 'ele'], 100], 0]],
        layout: {
          'symbol-placement': 'line',
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 10], [14, 10.5], [16, 12], [18, 13]]),
          'text-max-angle': 25,
          'text-padding': 8,
          'text-rotation-alignment': 'map'
        },
        paint: { 'text-color': C.contourLabel, 'text-halo-color': C.haloContour, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },

      // ---------- Wasser ----------
      fill('water-fill', 'water', ['!=', ['get', 'brunnel'], 'tunnel'], C.water),
      line('water-edge', 'water', ['!=', ['get', 'brunnel'], 'tunnel'], {
        'line-color': C.waterEdge,
        'line-width': w([[6, 0.3], [10, 0.7], [14, 1.3]]),
        'line-opacity': 0.9
      }),
      line('water-river', 'waterway', ['match', ['get', 'class'], ['river', 'canal'], true, false], {
        'line-color': C.river,
        'line-width': w([[8, 0.6], [12, 1.8], [16, 4.5], [20, 12]])
      }),
      line('water-stream', 'waterway', ['match', ['get', 'class'], ['stream', 'ditch', 'drain'], true, false], {
        'line-color': C.river,
        'line-width': w([[12, 0.5], [16, 1.6], [20, 5]]),
        'line-opacity': 0.85
      }),

      // Schiffsrouten
      line('water-ferry', 'transportation', ['==', ['get', 'class'], 'ferry'], {
        'line-color': C.waterEdge,
        'line-width': w([[9, 0.6], [14, 1.2], [18, 2]]),
        'line-dasharray': [4, 3],
        'line-opacity': 0.8
      }),

      // ---------- Tunnel (gedämpft, unter den Strassen) ----------
      line('road-tunnel', 'transportation', ['all', ['==', ['get', 'brunnel'], 'tunnel'], ['match', ['get', 'class'], ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor'], true, false]], {
        'line-color': C.minorCase,
        'line-width': w([[12, 1.5], [16, 6], [20, 16]]),
        'line-dasharray': [2, 2],
        'line-opacity': 0.55
      }),

      // ---------- Wege ----------
      line('road-track', 'transportation', ['==', ['get', 'class'], 'track'], {
        'line-color': C.track,
        'line-width': w([[13, 0.6], [16, 1.3], [20, 3]]),
        'line-dasharray': [5, 2.5]
      }),
      // Trottoirs, Treppen, Velowege: braun gestrichelt
      line('road-path', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['!=', ['get', 'subclass'], 'path']], {
        'line-color': C.path,
        'line-width': w([[14, 0.7], [16, 1.4], [20, 4]]),
        'line-dasharray': [2, 2]
      }),
      // Wanderwege: auf der Landeskarte rot gestrichelt
      line('path-hiking', 'transportation', ['all', ['==', ['get', 'class'], 'path'], ['==', ['get', 'subclass'], 'path']], {
        'line-color': C.hiking,
        'line-width': w([[12, 0.8], [15, 1.3], [18, 2.2], [20, 3.4]]),
        'line-dasharray': [3, 2]
      }),

      // ---------- Flugplätze ----------
      fill('aeroway-area', 'aeroway', ['match', ['get', 'class'], ['runway', 'taxiway', 'apron'], true, false], C.aeroway),

      ...roadLayers(C),

      // ---------- Bahn ----------
      // Feste Strichstaerke, bewusst ohne Zoomrampe: eine Liniensignatur hat
      // auf dem gedruckten Blatt eine Breite, nicht mehrere. Strecke und
      // Bahnhofsareal werden gleich dick gezeichnet - sie unterscheiden sich
      // nur darin, ab wann sie erscheinen.
      line('rail-base', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['!=', ['get', 'brunnel'], 'tunnel'], ['!', ['has', 'service']]], {
        'line-color': C.rail,
        'line-width': RAIL_W
      }),
      // Rangier-, Abstell- und Verbindungsgleise tragen in den Kacheln ein
      // `service`-Merkmal - bei Luzern die Haelfte aller Gleise. Alle zugleich
      // gezeichnet legen sie schon bei Zoom 13 einen roten Faecher ueber den
      // Bahnhof, deshalb kommen sie erst im groesseren Massstab dazu.
      {
        ...line('rail-service', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['!=', ['get', 'brunnel'], 'tunnel'], ['has', 'service']], {
          'line-color': C.rail,
          'line-width': RAIL_W
        }),
        minzoom: 14
      },
      // Bahntunnel: rot gestrichelt wie im Kartenbild, gleiche Breite
      line('rail-tunnel', 'transportation', ['all', ['==', ['get', 'class'], 'rail'], ['==', ['get', 'brunnel'], 'tunnel']], {
        'line-color': C.rail,
        'line-width': RAIL_W,
        'line-dasharray': [3, 2.5],
        'line-opacity': 0.85
      }),
      // Tram, Metro, Standseilbahn: eigene, schlankere Signatur - ebenfalls fest
      line('rail-light', 'transportation', ['match', ['get', 'class'], ['transit'], true, false], {
        'line-color': C.railLight,
        'line-width': RAIL_LIGHT_W,
        'line-dasharray': [4, 2]
      }),

      // ---------- Gebäude ----------
      // Zoom 13 ist die unterste Stufe mit vollstaendigem Bestand. Die Kachel
      // meldet dort zwar nur *ein* Objekt - aber Planetiler verschmilzt auf
      // dieser Stufe alle benachbarten Gebaeude zu einem Mehrfachpolygon mit
      // tausenden Ringen, das Bild ist also komplett. Bei Zoom 12 ist die
      // Ebene wirklich leer, tiefer geht es nicht.
      //
      // Alle gemeinsam ein- und ausblenden, nicht stufenweise: sonst wirken
      // Einfamilienhausquartiere leer, obwohl sie dicht bebaut sind.
      // Eine Ebene, eine Kurve.
      //
      // Unterhalb Zoom 14 fuehrt das Archiv die Gebaeude nur als wenige
      // verschmolzene Bloecke (gemessen: 4 statt 167). Die mitzuzeichnen und
      // bei 14 gegen die feine Fassung zu tauschen hiesse, eine Formaenderung
      // zu verstecken - und die faellt auf, egal wie gut die Deckkraft passt.
      // Zwei gleichzeitige Kachelstufen kann MapLibre je Quelle nicht.
      //
      // Deshalb: gar keine groben Bloecke. Bis 14 traegt die Siedlungsflaeche,
      // ab 14 blenden die einzelnen Gebaeude auf.
      {
        ...fill('building-fill', 'building', null, C.building, {
          'fill-opacity': GEBAEUDE_DECKUNG
        }),
        minzoom: 14
      },

      // Nur im hellen Bild. Bei fast weisser Fuellung ist die Kante nicht
      // Zierde, sondern das Einzige, was die Haeuser sichtbar macht - deshalb
      // ab derselben Stufe wie die Fuellung und etwas kraeftiger.
      ...(C.buildingEdge
        ? [{
            ...line('building-edge', 'building', null, {
              'line-color': C.buildingEdge,
              'line-width': w([[13, 0.5], [15, 0.7], [17, 1]]),
              // Dieselbe Rampe wie die Fuellung, sonst steht die Kante allein da.
              'line-opacity': GEBAEUDE_DECKUNG
            }),
            // Dieselbe Grenze wie die Fuellung: unterhalb liegen im Archiv nur
            // die verschmolzenen Bloecke, und die zeichnen wir nicht mehr.
            minzoom: 14
          }]
        : []),

      // Baukoerper. Standardmaessig aus - die Landeskarte ist ein flaches,
      // gezeichnetes Bild; 3D ist ein Zusatz, keine Grundeinstellung.
      // `hide_3d` markiert Teilflaechen, die sonst doppelt aufragen wuerden.
      {
        id: 'building-3d',
        type: 'fill-extrusion',
        source: 'osm',
        'source-layer': 'building',
        minzoom: 15,
        filter: ['!=', ['get', 'hide_3d'], true],
        layout: { visibility: 'none' },
        paint: {
          'fill-extrusion-color': C.building3d || C.building,
          'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 5],
          'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
          // Beim Hineinzoomen aufblenden, sonst springen die Haeuser auf.
          'fill-extrusion-opacity': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.8, 0.92]
        }
      },

      // ---------- Grenzen ----------
      line('boundary-canton-band', 'boundary', ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderBand,
        'line-width': w([[8, 1.5], [11, 3], [14, 5], [18, 9]]),
        'line-opacity': 0.4,
        'line-blur': 1
      }),
      line('boundary-canton', 'boundary', ['all', ['==', ['get', 'admin_level'], 4], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCanton,
        'line-width': w([[5, 0.4], [11, 0.8], [18, 2.4]]),
        'line-opacity': 0.6
      }),
      // Erst der Saum, dann der Strich darueber - so liest sich die Grenze auch
      // dort noch, wo sie durch dichte Bebauung laeuft.
      line('boundary-country-band', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderBand,
        'line-width': w([[4, 2.5], [8, 6], [12, 11], [16, 20]]),
        'line-opacity': 0.55,
        'line-blur': 1
      }),
      line('boundary-country', 'boundary', ['all', ['<=', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]], {
        'line-color': C.borderCountry,
        'line-width': w([[3, 0.6], [8, 1.1], [14, 1.8]]),
        'line-opacity': 0.9
      }),

      // ---------- Beschriftung ----------
      {
        id: 'label-water',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'water_name',
        minzoom: 7,
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[8, 11], [14, 15], [18, 20]]),
          'text-max-width': 7,
          'symbol-placement': 'point'
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-waterway',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 12,
        filter: ['match', ['get', 'class'], ['river', 'canal'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[12, 10], [14, 11.5], [18, 15]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.1
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-stream',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'waterway',
        minzoom: 14,
        filter: ['==', ['get', 'class'], 'stream'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 9.5], [16, 11.5], [18, 13]]),
          'symbol-placement': 'line',
          'text-letter-spacing': 0.08,
          'symbol-spacing': 400
        },
        paint: { 'text-color': C.waterLabel, 'text-halo-color': C.haloWater, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      // Hausnummern. Die Kacheln fuehren sie ab Zoom 14 (175 allein in der
      // Kachel Luzern), swisstopo zeigt sie ab 17 - frueher wird es Brei.
      {
        id: 'label-housenumber',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'housenumber',
        minzoom: 17,
        layout: {
          'text-field': ['get', 'housenumber'],
          'text-font': FONT,
          'text-size': ts([[17, 11], [19, 13]]),
          'text-padding': 2,
          'text-allow-overlap': false
        },
        paint: {
          'text-color': C.houseNumber,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1),
          'text-halo-blur': 0.25
        }
      },
      // Autobahnnummern. Die Kacheln fuehren diese Abschnitte meist ohne Namen,
      // nur mit `ref` ("A2") - unsere Beschriftung fragte bisher allein nach
      // dem Namen, deshalb blieben die Autobahnen unbeschriftet.
      {
        id: 'label-motorway',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'transportation_name',
        minzoom: 9,
        filter: ['all',
          ['match', ['get', 'class'], ['motorway', 'trunk'], true, false],
          ['has', 'ref']
        ],
        layout: {
          // Mehrfachnummern stehen als "24;25" - die erste genuegt.
          'text-field': ['slice', ['get', 'ref'], 0,
            ['case', ['>', ['index-of', ';', ['get', 'ref']], -1],
              ['index-of', ';', ['get', 'ref']], ['length', ['get', 'ref']]]],
          'text-font': FONT_MEDIUM,
          'text-size': ts([[9, 10], [13, 12], [16, 14]]),
          'symbol-placement': 'line',
          'text-rotation-alignment': 'map',
          'text-padding': 30,
          'symbol-spacing': 320
        },
        paint: {
          'text-color': C.motorwayCase,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1),
          'text-halo-blur': 0.25
        }
      },
      {
        id: 'label-road',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'transportation_name',
        minzoom: 13,
        filter: ['match', ['get', 'class'], ['ferry', 'path', 'track'], false, true],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([[13, 10], [16, 12], [18, 14]]),
          'symbol-placement': 'line',
          'text-rotation-alignment': 'map',
          'text-padding': 4
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-peak',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'mountain_peak',
        minzoom: 10,
        layout: {
          'text-field': ['format', NAME, {}, '\n', {}, ['concat', ['to-string', ['get', 'ele']], ' m'], { 'font-scale': 0.8 }],
          'text-font': FONT,
          'text-size': ts([[10, 10], [13, 12], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.7, 0],
          'icon-image': 'triangle_11',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.55, 16, 0.85],
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'poi-minor',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 16,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-minor']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': ICON,
          'text-field': ['step', ['zoom'], '', 17, NAME],
          'text-font': FONT,
          'text-size': ts([[13, 10.5], [18, 14]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.75],
          'text-max-width': 8,
          'text-optional': true
        },
        paint: {
          // Bahnhoefe im Bahnrot (bzw. Dunkelblau im hellen Modus), alles
          // Uebrige in der Farbe seiner Gruppe - Normalfall fast schwarz.
          'text-color': ['match', ['get', 'class'],
            ['railway', 'rail'], C.stationLabel, poiFarbe(C)],
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.9
        }
      },
      {
        id: 'poi-symbol',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'poi',
        minzoom: 14,
        filter: ['all', ['==', ['geometry-type'], 'Point'], POI_SPLIT['poi-symbol']],
        layout: {
          'icon-image': ['coalesce', ['image', ['get', 'class']], ['image', 'dot_11']],
          'icon-size': ICON,
          'text-field': ['step', ['zoom'], '', 15, NAME],
          'text-font': FONT,
          'text-size': ts([[7, 10], [13, 12], [18, 17]]),
          'text-anchor': 'top',
          'text-offset': [0, 0.8],
          'text-max-width': 8,
          'text-optional': true,
          'icon-allow-overlap': false
        },
        paint: {
          // Bahnhoefe im Bahnrot (bzw. Dunkelblau im hellen Modus), alles
          // Uebrige in der Farbe seiner Gruppe - Normalfall fast schwarz.
          'text-color': ['match', ['get', 'class'],
            ['railway', 'rail'], C.stationLabel, poiFarbe(C)],
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-opacity': 0.95
        }
      },
      {
        id: 'spot-elevation',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'spot_elevation',
        minzoom: 13,
        layout: {
          'text-field': ['to-string', ['get', 'ele']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 11], [14, 11.5], [16, 14], [18, 15]]),
          'text-anchor': 'left',
          'text-offset': [0.5, 0],
          'icon-image': 'dot_9',
          'icon-size': ['interpolate', ['linear'], ['zoom'], 13, 0.45, 18, 0.7],
          'text-optional': true
        },
        paint: {
          'text-color': C.peak,
          'text-halo-color': C.halo,
          'text-halo-width': haloW(1), 'text-halo-blur': 0.25,
          'icon-color': C.peak,
          'icon-halo-color': C.halo,
          'icon-halo-width': haloW(1)
        }
      },
      {
        id: 'label-area-name',
        type: 'symbol',
        source: 'swisstopo',
        'source-layer': 'area_name',
        minzoom: 13,
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ['get', 'name:de']],
          'text-font': FONT_ITALIC,
          'text-size': ts([[13, 13], [18, 19]]),
          'text-max-width': 7,
          'text-letter-spacing': 0.04
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloW(1), 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-small',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 11,
        // 'locality' und 'farm' greifen heute noch nicht: das OpenMapTiles-Schema
        // kennt diese Klassen nicht, deshalb landen Flurnamen gar nicht erst in
        // den Kacheln. Mit einem eigenen Planetiler-Schema wuerden sie erscheinen.
        filter: ['match', ['get', 'class'],
          ['village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'isolated_dwelling',
           'locality', 'farm', 'island'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT,
          'text-size': ts([[11, 11], [14, 14], [18, 19]]),
          'text-max-width': 8
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-town',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 8,
        filter: ['match', ['get', 'class'], ['town'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_MEDIUM,
          'text-size': ts([[8, 12], [13, 17], [17, 24]]),
          'text-max-width': 8,
          // In der Uebersicht gemischt (Kriens, Horw), im groesseren Massstab
          // tritt der Hauptort in Versalien hervor.
          'text-transform': ['step', ['zoom'], 'none', 12, 'uppercase'],
          'text-letter-spacing': ['step', ['zoom'], 0, 12, 0.09]
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-place-city',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        minzoom: 4,
        filter: ['match', ['get', 'class'], ['city'], true, false],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([[4, 13], [10, 22], [13, 30]]),
          'text-max-width': 9,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.12
        },
        paint: { 'text-color': C.ink, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      },
      {
        id: 'label-country',
        type: 'symbol',
        source: 'osm',
        'source-layer': 'place',
        maxzoom: 9,
        filter: ['==', ['get', 'class'], 'country'],
        layout: {
          'text-field': NAME,
          'text-font': FONT_BOLD,
          'text-size': ts([[3, 11], [7, 16]]),
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.18,
          'text-max-width': 7
        },
        paint: { 'text-color': C.inkSoft, 'text-halo-color': C.halo, 'text-halo-width': haloPlace, 'text-halo-blur': 0.25 }
      }
    ])
  };
}
